/**
 * In-flight composer attachment. Created the moment a file is added (drag,
 * paste, picker) so the chip renders optimistically; transitions to `ready`
 * once the upload completes and the server returns a remote id.
 */
export interface PendingAttachment {
  localId: string;
  file: File;
  status: 'uploading' | 'ready' | 'error';
  kind: 'image' | 'document';
  remoteId?: string;
  errorMessage?: string;
  previewUrl?: string;
}

export const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export const ALLOWED_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'text/html',
  'text/markdown',
  'text/plain'
] as const;
export const ALLOWED_MIME_TYPES = [...ALLOWED_IMAGE_MIME_TYPES, ...ALLOWED_DOCUMENT_MIME_TYPES] as const;

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_MESSAGE = 3;

export const isImageMime = (mime: string): boolean => (ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(mime);
export const isAllowedMime = (mime: string): boolean => (ALLOWED_MIME_TYPES as readonly string[]).includes(mime);

export const validateFile = (file: File): { ok: true } | { ok: false; reason: string } => {
  if (!isAllowedMime(file.type)) {
    return { ok: false, reason: `Unsupported file type: ${file.type || 'unknown'}` };
  }
  const isImage = isImageMime(file.type);
  const max = isImage ? MAX_IMAGE_BYTES : MAX_DOCUMENT_BYTES;
  if (file.size > max) {
    const limitMb = Math.floor(max / (1024 * 1024));
    return {
      ok: false,
      reason: `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max ${limitMb} MB.`
    };
  }
  return { ok: true };
};
